import React, { useState, useEffect } from 'react';
import BiologicalLayout from '../../components/layout/BiologicalLayout';
import { useAuth, API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  FileText, TrendingUp, ChevronDown, ChevronUp, Activity,
} from 'lucide-react';
import axios from 'axios';

const SIGNAL_STYLES = {
  green: { bg: 'bg-emerald-900/20', text: 'text-emerald-400', border: 'border-emerald-800/20', label: 'Normal' },
  amber: { bg: 'bg-amber-900/20', text: 'text-amber-400', border: 'border-amber-800/20', label: 'Borderline' },
  red: { bg: 'bg-red-900/20', text: 'text-red-400', border: 'border-red-800/20', label: 'Attention' },
  grey: { bg: 'bg-white/5', text: 'text-white/30', border: 'border-white/[0.06]', label: 'Pending' },
};

export default function MyLabs() {
  const { user } = useAuth();
  const [labData, setLabData] = useState(null);
  const [expandedReport, setExpandedReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      axios.get(`${API}/lab-v2/my-labs/${user.id}`).then(r => {
        setLabData(r.data);
        if (r.data.reports?.length > 0) setExpandedReport(r.data.reports[0].id);
      }).catch(() => {}).finally(() => setLoading(false));
    }
  }, [user?.id]);

  if (loading) {
    return <BiologicalLayout><div className="flex items-center justify-center h-64 text-white/30 text-sm">Loading lab results...</div></BiologicalLayout>;
  }

  if (!labData?.has_data) {
    return (
      <BiologicalLayout>
        <div className="max-w-3xl mx-auto p-6 space-y-6">
          <h1 className="text-xl font-semibold text-[#C89B7B]">My Labs</h1>
          <Card className="bg-white/[0.02] border-white/[0.06]">
            <CardContent className="p-12 text-center space-y-3">
              <Activity className="w-10 h-10 text-white/10 mx-auto" />
              <p className="text-white/40 text-sm">Your lab results will appear here once your care team has reviewed and validated them.</p>
              <p className="text-[10px] text-white/20">Lab reports are carefully reviewed before being shared with you.</p>
            </CardContent>
          </Card>
        </div>
      </BiologicalLayout>
    );
  }

  const { reports, marker_summary, signal_counts } = labData;

  return (
    <BiologicalLayout>
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-xl font-semibold text-[#C89B7B]">Labs / Biological Maps</h1>
          <p className="text-xs text-white/30 mt-1">Your reports are not just numbers. They help map your internal terrain.</p>
        </div>

        {/* Signal overview */}
        <div className="grid grid-cols-3 gap-3" data-testid="lab-signal-overview">
          <Card className={`border ${SIGNAL_STYLES.green.border} ${SIGNAL_STYLES.green.bg}`}>
            <CardContent className="p-4 text-center">
              <p className={`text-2xl font-semibold ${SIGNAL_STYLES.green.text}`}>{signal_counts.green}</p>
              <p className="text-xs text-white/40 mt-1">Normal</p>
            </CardContent>
          </Card>
          <Card className={`border ${SIGNAL_STYLES.amber.border} ${SIGNAL_STYLES.amber.bg}`}>
            <CardContent className="p-4 text-center">
              <p className={`text-2xl font-semibold ${SIGNAL_STYLES.amber.text}`}>{signal_counts.amber}</p>
              <p className="text-xs text-white/40 mt-1">Borderline</p>
            </CardContent>
          </Card>
          <Card className={`border ${SIGNAL_STYLES.red.border} ${SIGNAL_STYLES.red.bg}`}>
            <CardContent className="p-4 text-center">
              <p className={`text-2xl font-semibold ${SIGNAL_STYLES.red.text}`}>{signal_counts.red}</p>
              <p className="text-xs text-white/40 mt-1">Needs Attention</p>
            </CardContent>
          </Card>
        </div>

        {/* Reports by date */}
        <div className="space-y-3">
          <h2 className="text-sm text-white/50 flex items-center gap-2">
            <FileText className="w-4 h-4" /> Reports ({reports.length})
          </h2>
          {reports.map(report => {
            const expanded = expandedReport === report.id;
            return (
              <Card key={report.id} className="bg-white/[0.02] border-white/[0.06] overflow-hidden">
                <button className="w-full p-4 flex items-center justify-between text-left" data-testid={`report-${report.id}`}
                  onClick={() => setExpandedReport(expanded ? null : report.id)}>
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-white/20" />
                    <div>
                      <p className="text-sm text-white/80">{report.report_date}</p>
                      <p className="text-[10px] text-white/30">{report.source_lab || 'Lab Report'} — {report.marker_count} markers</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {report.green_count > 0 && <Badge className="text-[7px] bg-emerald-900/20 text-emerald-400">{report.green_count} normal</Badge>}
                    {report.amber_count > 0 && <Badge className="text-[7px] bg-amber-900/20 text-amber-400">{report.amber_count} borderline</Badge>}
                    {report.red_count > 0 && <Badge className="text-[7px] bg-red-900/20 text-red-400">{report.red_count} attention</Badge>}
                    {expanded ? <ChevronUp className="w-4 h-4 text-white/20" /> : <ChevronDown className="w-4 h-4 text-white/20" />}
                  </div>
                </button>
                {expanded && (
                  <div className="px-4 pb-4 border-t border-white/[0.04]">
                    <table className="w-full text-xs mt-3">
                      <thead>
                        <tr className="text-white/25 text-[9px]">
                          <th className="py-1.5 text-left">Marker</th>
                          <th className="py-1.5 text-right">Result</th>
                          <th className="py-1.5 text-left pl-2">Unit</th>
                          <th className="py-1.5 text-left">Normal Range</th>
                          <th className="py-1.5 text-left">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.markers.map((m, idx) => {
                          const s = SIGNAL_STYLES[m.signal] || SIGNAL_STYLES.grey;
                          return (
                            <tr key={idx} className="border-t border-white/[0.03]">
                              <td className="py-2 text-white/70">{m.name}</td>
                              <td className={`py-2 text-right font-medium ${s.text}`}>{m.value ?? '-'}</td>
                              <td className="py-2 pl-2 text-white/25">{m.unit}</td>
                              <td className="py-2 text-white/20">
                                {m.reference_min != null && m.reference_max != null ? `${m.reference_min} - ${m.reference_max}` : '-'}
                              </td>
                              <td className="py-2">
                                <Badge className={`text-[7px] ${s.bg} ${s.text}`}>{s.label}</Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        {/* Key marker summary */}
        {marker_summary.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-sm text-white/50 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Key Markers (Latest)
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {marker_summary.slice(0, 12).map((m, i) => {
                const s = SIGNAL_STYLES[m.signal] || SIGNAL_STYLES.grey;
                return (
                  <Card key={i} className={`border ${s.border} ${s.bg}`}>
                    <CardContent className="p-3">
                      <p className="text-[9px] text-white/30 truncate">{m.name}</p>
                      <p className={`text-lg font-semibold ${s.text} mt-0.5`}>{m.latest_value ?? '-'}</p>
                      <p className="text-[8px] text-white/20">{m.unit} — {s.label}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        <p className="text-center text-[9px] text-white/10 pt-4">
          Lab data reviewed and validated by your care team. For clinical decisions, always consult your physician.
        </p>
      </div>
    </BiologicalLayout>
  );
}
